import React, { useEffect, useState, useCallback } from "react";
import { Row, Col, Form, Table, Button } from "react-bootstrap";
import plusCircleIcon from "../../../assets/images/plusCircleIcon.svg";
import redCloseIcon from "../../../assets/images/red-close-icon.svg"
import {
  getOptions,
  getLevel2Options,
  getLevel3Options,
  optionsData,
} from "./constants";
import { ProjectService } from "../../../services";
import useDebouncedCallback from "../../../hooks/useDebounce";
import SelectField, { OptionType } from "./SelectField";
import "./FscProductGroupList.css";
import ModalComponent from "../../Modal";
interface FscProductGroupListProps {
  section34Data: any;
  formData: any;
  setFormData: React.Dispatch<React.SetStateAction<any>>;
}


const fscProductGroupsComparisonOptions = [
  {
    label: "--None--",
    value: null,
  },
  {
    label: "Organization's Product Groups are accurately reflected in the FSC database, no changes.",
    value: "Matched",
  },
  {
    label: "Changes required, see “SECTION 2: CONFIRMATION OF CHANGES” (above) with list of changes.",
    value: "Is attached as Annex",
  },
];


const FscProductGroupList: React.FC<FscProductGroupListProps> = ({
  section34Data,
  formData,
  setFormData,
}) => {
  const [searchedSpeciesLatinName, setSearchedSpeciesLatinName] = useState<
    OptionType[]
  >([]);
  const [productRows, setProductRows] = useState<any>([]);
  const [certificateoptions, setCertificateoptions] = useState<any>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState<string[]>([]);
  const [selectedClaims, setSelectedClaims] = useState<number[][]>(
    Array(productRows.length).fill([])
  );
  const [searchStatus, setSearchStatus] = useState<
    "loading" | "error" | "success"
  >("success");


  function splitSpeciesString(speciesString: string): string[] {
    // Helper: extract all Synonyms blocks and their content, return updated string without those blocks and a map of index to synonyms list
    function extractAllSynonyms(input: string): { cleanedString: string; synonymsMap: Map<number, string[]> } {
      let cleanedString = '';
      let synonymsMap = new Map<number, string[]>();

      let i = 0;
      while (i < input.length) {
        let synStart = input.indexOf('(Syn.:', i);
        if (synStart === -1) {
          // No more Synonyms blocks
          cleanedString += input.slice(i);
          break;
        }

        // Add everything before (Syn.: block
        cleanedString += input.slice(i, synStart);

        // Now find the matching closing ')', considering nested parentheses
        let j = synStart + 6; // after (Syn.:
        let parenthesesLevel = 1;
        while (j < input.length && parenthesesLevel > 0) {
          if (input[j] === '(') parenthesesLevel++;
          else if (input[j] === ')') parenthesesLevel--;
          j++;
        }
        // Extract the synonyms content inside parentheses (excluding trailing ')')
        const synContent = input.slice(synStart + 6, j - 1);

        // Split and trim synonyms by commas
        const synonyms = synContent.split(',').map(s => s.trim()).filter(s => s.length > 0);

        // Map the position in cleanedString to synonyms list
        synonymsMap.set(cleanedString.length, synonyms);

        i = j; // Continue from after the closing ')'
      }

      return { cleanedString, synonymsMap };
    }

    // Step 1: Extract all synonyms blocks and clean string
    const { cleanedString, synonymsMap } = extractAllSynonyms(speciesString);

    // Step 2: Split cleaned string by commas (ignoring parentheses)
    const result: string[] = [];
    let current = '';
    let parenthesesLevel = 0;

    for (let i = 0; i < cleanedString.length; i++) {
      const char = cleanedString[i];
      if (char === '(') {
        parenthesesLevel++;
        current += char;
      } else if (char === ')') {
        parenthesesLevel = Math.max(0, parenthesesLevel - 1);
        current += char;
      } else if (char === ',' && parenthesesLevel === 0) {
        if (current.trim()) result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    if (current.trim()) result.push(current.trim());

    // Step 3: Insert synonyms at proper positions
    // Because synonymsMap keys represent positions in cleanedString, 
    // map them to indexes in the `result` array by accumulating length of each species + comma + spaces

    let accumulatedLength = 0;
    let finalResult: string[] = [];
    let synEntriesIndex = 0;

    for (const entry of result) {
      // Add the main species entry
      finalResult.push(entry);

      accumulatedLength += entry.length;
      // After each species entry (except maybe the last), there's likely a comma + space in original cleanedString
      accumulatedLength += 2; // approximate for ", "

      // Check if synonyms exist at or after this accumulatedLength
      for (const [pos, syns] of synonymsMap.entries()) {
        if (pos >= accumulatedLength - entry.length - 2 && pos <= accumulatedLength) {
          finalResult.push(...syns);
          synonymsMap.delete(pos); // prevent duplicates
        }
      }
    }

    // Add any remaining synonyms not inserted yet (unlikely but safe)
    for (const syns of synonymsMap.values()) {
      finalResult.push(...syns);
    }

    return finalResult;
  }


  // Debounced search for species latin names
  const handleSearchSpeciesLatinName = useDebouncedCallback(async (value: string) => {
    if (value.length < 3) return;
    setSearchStatus("loading");
    try {
      const response = await ProjectService.getSection4SpeciesLatinName(value);
      const speciesLatinName = response.species.map((item: any) => ({
        id: item.id,
        label: item.Name,
        value: item.Name,
        certifiedProductId: null,
        certifiedSpeciesId: null,
      }));

      setSearchedSpeciesLatinName((prev) => {
        const existingIds = new Set(prev.map((item) => item.id));
        const newItems = speciesLatinName.filter((item: any) => !existingIds.has(item.id));
        return [...newItems, ...prev];
      });

      setSearchStatus("success");
    } catch {
      setSearchStatus("error");
    }
  }, 300);

  const data = {
    productCodeL1: optionsData.productCodeL1,
    speciesLatinName: searchedSpeciesLatinName,
    primaryActivity: optionsData.primaryActivity,
    secondaryActivity: optionsData.secondaryActivity,
    fscOutputClaim: optionsData.fscOutputClaim,
  };

  // Centralized handler to update form data rows
  const handleDropdownChange = useCallback(
    (rowIndex: number, field: string, value: any) => {
      console.log(field)
      setFormData((prev: any) => {
        const updatedRows = [...(prev.productRows || [])];
        if (!updatedRows[rowIndex]) updatedRows[rowIndex] = {};
        updatedRows[rowIndex][field] = value === "--None--" ? null : value;
        // Reset dependent selects if needed
        if (field === "productCodeL1") {
          updatedRows[rowIndex].productCodeL2 = null;
          updatedRows[rowIndex].productCodeL3 = null;
        } else if (field === "productCodeL2") {
          updatedRows[rowIndex].productCodeL3 = null;
        }

        return { ...prev, productRows: updatedRows };
      });
      setProductRows((prev: any) => {
        const updatedRows = [...(prev || [])];
        if (!updatedRows[rowIndex]) updatedRows[rowIndex] = {};
        updatedRows[rowIndex][field] = value === "--None--" ? null : value;
        // Reset dependent selects if needed
        if (field === "productCodeL1") {
          updatedRows[rowIndex].productCodeL2 = null;
          updatedRows[rowIndex].productCodeL3 = null;
        } else if (field === "productCodeL2") {
          updatedRows[rowIndex].productCodeL3 = null;
        }

        console.log(updatedRows)
        return updatedRows;
      });
    },
    [setFormData]
  );

  const handleLatinNameChange = (rowIndex: number, option: any) => {
    if (!option) return;
    let selectedLatinName = [...productRows[rowIndex].speciesLatinName];
    if (selectedLatinName.every((item: any) => item.id !== option.id)) {
      selectedLatinName = [...selectedLatinName, option]
    } else {
      selectedLatinName = selectedLatinName.filter(item => item.id !== option.id);
    }

    const updatedRows = [...productRows];
    updatedRows[rowIndex] = {
      ...updatedRows[rowIndex],
      speciesLatinName: selectedLatinName,
    };
    console.log("updatedRows", updatedRows);
    setProductRows(updatedRows);

    setFormData((prev: any) => {
      return { ...prev, productRows: updatedRows };
    });
  };




  const handleLabelClick = (rowIndex: number, option: OptionType) => {
    let selectedClaims: any = productRows[rowIndex].fscOutputClaim ? [...productRows[rowIndex].fscOutputClaim] : [];
    if (selectedClaims.every((item: any) => item.id !== option.id)) {
      const formatedOption = {
        id: option.label,
        label: option.label,
        value: option.label
      }
      selectedClaims = [...selectedClaims, { id: option.label, label: option.label, value: option.label }]
    } else {
      selectedClaims = selectedClaims.filter((item: any) => item.id !== option.id);
    }

    console.log(selectedClaims)

    const updatedRows = [...productRows];
    updatedRows[rowIndex] = {
      ...updatedRows[rowIndex],
      fscOutputClaim: selectedClaims,
    };
    console.log("updatedRows", updatedRows);
    setProductRows(updatedRows);
    setFormData((prev: any) => {
      return { ...prev, productRows: updatedRows };
    });
  }


  const handleCheckboxChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev: any) => ({
        ...prev,
        PGL_FSC_as_seperate_record__c: e.target.checked
      }));
    },
    [setFormData]
  );

  const onChange = useCallback(
    (newData: Partial<any>) => {
      setFormData((prev: any) => ({ ...prev, ...newData }));
    },
    [setFormData]
  );

  const handleAddNewRow = () => {
    setProductRows((prev: any) => [...prev, {
      certificateId: "",
      productCodeL1: null,
      productCodeL2: null,
      productCodeL3: null,
      speciesCommonName: "",
      speciesLatinName: [],// array of species names as object
      primaryActivity: null,
      secondaryActivity: null,
      certifiedProductId: null,
      fscOutputClaim: []
    }]);
    setFormData((prev: any) => ({
      ...prev,
      productRows: [...(prev.productRows || []), {
        certificateId: "",
        productCodeL1: null,
        productCodeL2: null,
        productCodeL3: null,
        speciesCommonName: "",
        speciesLatinName: [],// array of species names as object
        primaryActivity: null,
        secondaryActivity: null,
        certifiedProductId: null,
        fscOutputClaim: []
      }],
    }));
  };

  const tableHeaders = [
    { label: "Certificate ID" },
    { label: <>Product Code L1<span className="text-danger">*</span></>, subLabel: "(per FSC-STD-40-004a)" },
    { label: "Product Code L2", subLabel: "(per FSC-STD-40-004a)" },
    { label: "Product Code L3", subLabel: "(per FSC-STD-40-004a)" },
    { label: "Trade Name", subLabel: "(Common name)" },
    { label: "Species", subLabel: "" },
    { label: "Primary Activity", subLabel: "(choose number(s) from table below)" },
    { label: "Secondary Activity", subLabel: "(choose number(s) from table below)" },
    { label: "FSC Output Claim", subLabel: "(FSC 100%, FSC Mix X%, etc.)" },
  ];

  const splitAndFormOutputCategory = (cat: string) => {
    const catArr = cat.split(";");
    const outputCat = catArr.map((item: string) => ({
      id: item,
      label: item,
      value: item,
    }));

    return outputCat;
  }

  useEffect(() => {
    if (section34Data?.opportunityCertificates?.length > 0) {
      let filteredProductRows: any = [];
      const formCertficateOptions: any = [];
      section34Data.opportunityCertificates.forEach((item: any) => {
        if (item.Certificate_Number__c) {
          formCertficateOptions.push({
            id: item.Certificate_Number__c,
            label: item.Certificate_Number__c,
            value: item.Certificate_Number__c,
          })
        }
        const certifiedProducts = item.Certified_Products__r?.records || [];
        const initialRows = certifiedProducts.map((product: any, rowIndex: number) => {
          const relatedSpeciesStr = product.Related_Species__c || '';
          const parsedSpecies = splitSpeciesString(relatedSpeciesStr);

          const speciesLatinName = product.Certified_Species__r?.records.map((item: any) => ({
            id: item.Species__c,
            label: item.Species__r.Name,
            value: item.Species__r.Name,
            certifiedProductId: item.Certified_Product__c,
            certifiedSpeciesId: item.id
          }));

          return {
            certificateId: product.Certificate_Number__c || "",
            productCodeL1: product.Level_1__c || null,
            productCodeL2: product.Level_2__c || null,
            productCodeL3: product.Level_3__c || null,
            speciesCommonName: product.Trade_Name__c || "",
            speciesLatinName: speciesLatinName || [],// array of species names as object
            primaryActivity: product.Primary_Activity__c || null,
            secondaryActivity: product.Secondary_Activity__c || null,
            certifiedProductId: product.id || null,
            fscOutputClaim: product.Output_Category__c ? splitAndFormOutputCategory(product.Output_Category__c) : [], // assuming multiselect
          };
        });
        filteredProductRows = [...filteredProductRows, ...initialRows]
      })

      console.log("initialRows", filteredProductRows);

      setProductRows(filteredProductRows);

      setSelectedClaims(filteredProductRows.map((row: any) => row.fscOutputClaim));

      setFormData((prev: any) => ({
        ...prev,
        productRows: filteredProductRows
        // selectedClaims: initialRows.map((row:any) => row.fscOutputClaim || []),
      }));
      console.log(formCertficateOptions)
      setCertificateoptions(formCertficateOptions);
    }
  }, [section34Data, setFormData]);

  const handleRadioChange = (value: string | null) => {
    setFormData((prev: any) => ({
      ...prev,
      Product_List_Comparison_SCS_FSC__c: value,
    }));
  };

  const handleDeleteRow = () => {
    const updatedRows = productRows.filter((item: any, index: number) => !selectedRow.includes(String(index)));
    setProductRows(updatedRows);
    setFormData((prev: any) => {
      return { ...prev, productRows: updatedRows };
    });
    setShowDeleteModal(false);
  };

  const handleProductRowChecked = (rowIndex: string) => {
    console.log(rowIndex)
    if (selectedRow.includes(rowIndex)) {
      setSelectedRow((prev: any) => {
        return prev.filter((item: string) => item !== rowIndex);
      });
    } else {
      setSelectedRow((prev: any) => {
        return [...prev, String(rowIndex)];
      });
    }
  }


  const handleDeleteRoqModal = () => {
    setShowDeleteModal(true);
  }




  return (
    <>
      <div className="fsc-product-group-header">
        <Form.Label className="me-2" style={{ marginBottom: 4 }}>
          FSC Product Group List
        </Form.Label>
        <Form.Check
          type="checkbox"
          id="na-checkbox"
          label="N/A"
          className="na-checkbox"
          checked={formData.FSC_Product_Group_List__c}
          onChange={(e) => onChange({ FSC_Product_Group_List__c: e.target.checked })}
        />
      </div>

      <div className="p-4">
        <div>
          {fscProductGroupsComparisonOptions.map((option) => (
            <Form.Check key={String(option.value)}>
              <Form.Check.Input
                type="radio"
                name="Product_List_Comparison_SCS_FSC__c"
                value={option.value ?? "null"} // <-- convert null to string
                checked={
                  String(formData.Product_List_Comparison_SCS_FSC__c) === String(option.value)
                }
                onChange={(e) => handleRadioChange(e.target.value === "null" ? null : e.target.value)}

                className="product-category-check-input"
              />
              <Form.Check.Label className="product-categories-label">
                {option.label}
              </Form.Check.Label>
            </Form.Check>
          ))}

        </div>


        <hr />

        <Row className="mb-4">
          <Col>
            <Form.Group style={{ lineHeight: "19.2px" }}>
              <Form.Label>
                2. The PGL must be included within this summary report. If PGL exceeds 15 entries, submit an additional pdf with remaining PGLs.
              </Form.Label>
              <div>
                <span style={{ fontWeight: 800, fontSize: 16, color: "#353535" }}>
                  Note:
                </span>
                <Form.Text style={{ fontSize: 16, color: "#353535" }}>
                  The client's full written procedures cannot be attached, the PGL must be included separate from the organization's written procedures as a record or excel spreadsheet. All required information from the table below must be included in the attachment.
                </Form.Text>
              </div>
            </Form.Group>
          </Col>
        </Row>

        <div className="d-flex justify-content-between mt-3">
          <p className="fsc-form-selection-info">PGL table is completed below.</p>
        </div>

        <Form.Label className="mt-1 mb-3">
          Please select one of the following in order to provide PGL information:
        </Form.Label>

        <Row>
          <Col>
            <Table bordered className="fsc-table" responsive>
              <thead>
                <tr>
                  {tableHeaders.map((header, idx) => (
                    <th key={idx} className="fsc-table-header">
                      <p className="fsc-table-header-label">{header.label}</p>
                      <span className="fsc-table-header-sublabel">{header.subLabel}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {productRows.map((row: any, rowIndex: number) => (
                  <tr key={rowIndex}>
                    <td>
                      <div className="d-flex">
                        <Form.Check
                          type="checkbox"
                          name="Product"
                          checked={selectedRow.includes(String(rowIndex))}
                          onChange={() => handleProductRowChecked(String(rowIndex))}
                          className="m-1 product-column-checkbox"
                        />
                        <SelectField
                          label="Select Certificate ID"
                          data={certificateoptions}
                          selectedValue={row.certificateId}
                          onChange={(_, option) =>
                            handleDropdownChange(rowIndex, "certificateId", option.value)
                          }
                        />
                      </div>
                    </td>

                    <td>
                      <SelectField
                        label="Select Product Code"
                        data={data.productCodeL1}
                        selectedValue={row.productCodeL1 === null ? "--None--": row.productCodeL1}
                        onChange={(_, option) =>
                          handleDropdownChange(rowIndex, "productCodeL1", option.value)
                        }
                      />
                    </td>

                    <td>
                      <SelectField
                        label="Select product Code"
                        disabled={!row.productCodeL1}
                        data={getOptions(getLevel2Options(row.productCodeL1))}
                        selectedValue={row.productCodeL2 === null ? "--None--": row.productCodeL2}
                        onChange={(_, option) =>
                          handleDropdownChange(rowIndex, "productCodeL2", option.value)
                        }
                      />
                    </td>

                    <td>
                      <SelectField
                        label="Select product Code"
                        disabled={!row.productCodeL2}
                        data={getOptions(getLevel3Options(row.productCodeL1, row.productCodeL2))}
                        selectedValue={row.productCodeL3 === null ? "--None--": row.productCodeL3}
                        onChange={(_, option) =>
                          handleDropdownChange(rowIndex, "productCodeL3", option.value)
                        }
                      />
                    </td>

                    <td>
                      <Form.Control
                        type="text"
                        className="fsc-product-group-list-input"
                        placeholder="Enter Trade Name"
                        value={row.speciesCommonName || ""}
                        onChange={(e) =>
                          handleDropdownChange(rowIndex, "speciesCommonName", e.target.value)
                        }
                      />
                    </td>

                    <td>
                      <SelectField
                        label="Select Specie"
                        data={data.speciesLatinName}
                        selectedValue={row.speciesLatinName}
                        disabled={false}
                        as="typehead"
                        onSearch={handleSearchSpeciesLatinName}
                        searchStatus={searchStatus}
                        multiselect
                        onChange={(_, option) => handleLatinNameChange(rowIndex, option)}
                      />
                    </td>

                    <td>
                      <SelectField
                        label="Select Activity"
                        data={data.primaryActivity}
                        selectedValue={row.primaryActivity === null ? "--None--" : row.primaryActivity}
                        onChange={(_, option) =>
                          handleDropdownChange(rowIndex, "primaryActivity", option.value)
                        }
                      />
                    </td>

                    <td>
                      <SelectField
                        label="Select Activity"
                        data={data.secondaryActivity}
                        selectedValue={row.secondaryActivity === null ? "--None--" : row.secondaryActivity}
                        onChange={(_, option) =>
                          handleDropdownChange(rowIndex, "secondaryActivity", option.value)
                        }
                      />
                    </td>

                    <td>

                      <SelectField
                        label="Select Claim"
                        data={data.fscOutputClaim}
                        disabled={false}
                        onChange={(_, option) =>
                          handleLabelClick(rowIndex, option)
                        }
                        multiselect
                        selectedValue={row.fscOutputClaim}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Col>
        </Row>
        <div className="product-row-btn-container d-flex justify-content-end">
          <Button
            variant="link"
            onClick={() => handleDeleteRoqModal()}
            className="delete-row-btn"
            disabled={!selectedRow.length}
          >
            {selectedRow && selectedRow.length > 1 ? "Delete Rows" : "Delete Row"}
            <img
              style={{ width: 18, height: 18, marginLeft: 8 }}
              src={redCloseIcon}
              alt="Delete"
            />
          </Button>
          <Button
            variant="link"
            onClick={handleAddNewRow}
            className="add-row-btn me-2"
          >
            Add new Row
            <img
              style={{ width: 18, height: 18, marginLeft: 8 }}
              src={plusCircleIcon}
              alt="Add Row"
            />
          </Button>
        </div>
        <hr />
        <Form.Check
          type="checkbox"
          label="Organization's Product Group List is provided as a separate record."
          name="orgProductList"
          checked={formData.PGL_FSC_as_seperate_record__c}
          onChange={handleCheckboxChange}
          className="mt-3"
        />

        <div className="d-flex justify-content-between mt-3">
          <p className="instruction-text">
            *Please include the most up-to-date PGL. If product list exceeds 15,
            please submit additional document as attachment
          </p>
        </div>
      </div>
      <ModalComponent
        type="error"
        title="Delete Product"
        enableFooter={true}
        btnText="Delete Product"
        show={showDeleteModal}
        setShow={setShowDeleteModal}
        onConfirmModal={handleDeleteRow}>
        <p>"Are you sure you would like to permanently delete this Product from this summary report?" </p>
        <p><i>Note: This action cannot be undone.</i></p>
      </ModalComponent>
    </>
  );
};

export default FscProductGroupList;
